import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Field, SectionCard } from "./shared";
import BankAccountSelect, { decodeBankAccountSelection } from "./BankAccountSelect";
import AccountSelect, { decodeAccountSelection } from "./AccountSelect";
import VendorSelect from "./VendorSelect";
import PartySearchInput from "./PartySearchInput";
import { formatCurrency, deriveTransactionDetails } from "./LineGrid";
import { ACCOUNTING_BASE } from "./accountingNav";

// Phase 5 correction (Enforce Payment Nature and Account Consistency): a
// Payment must never let the system silently decide which of several
// distinct accounting outcomes applies to the same physical cash movement -
// the user states this explicitly via Payment Nature using professional
// categories, and the form then offers the ONE right, filtered field set
// for that choice. Never defaults to "Other" for a normal salary/vendor/
// payable transaction - the default below is "Direct Expense", the single
// most common real-world Payment.
const PAYMENT_NATURE_LABELS = {
  direct_expense: "Direct Expense",
  emergency_expense: "Emergency / No-Invoice Expense",
  salary_payment: "Salary Payment",
  reimbursement: "Staff / Member Reimbursement",
  payable_payment: "Payable Payment",
  advance_payment: "Advance Payment",
  capital_payment: "Asset / Capital Payment",
  refund: "Refund",
  other: "Other",
};
const PAYMENT_NATURE_HELP = {
  direct_expense: "No liability was booked earlier - this payment both incurs and settles an Expense right now (Dr Expense, Cr Bank). Do not use Sweeper/Vendor Payable here unless that liability already exists.",
  emergency_expense: "Use when urgent work was paid without a supplier invoice, such as emergency septic-tank cleaning. Select the Expense ledger and record the payee and full reason; the posted voucher is the Society's internal supporting record.",
  salary_payment: "Use for salary paid directly without first booking Salary Payable (Dr Salary Expense, Cr Bank/Cash). Enter the employee name and salary month in Narration.",
  reimbursement: "Use when staff or a member paid a Society expense personally and is now being reimbursed (Dr the actual Expense, Cr Bank/Cash). Enter who incurred it and what was purchased in Narration.",
  payable_payment: "An existing Vendor Bill or Payable liability is being cleared (Dr the Payable/Vendor ledger, Cr Bank). Never select this just because the payee sounds like a vendor - only when a real payable is already on the books.",
  advance_payment: "Money paid out ahead of a bill or service being rendered, tracked as a recoverable Advance (Dr an Advance/asset ledger, Cr Bank).",
  capital_payment: "A capital purchase - Fixed Deposit, Investment, Equipment - not a routine running expense (Dr the Asset ledger, Cr Bank).",
  refund: "Repaying money previously received from a Member or Vendor, or against a Liability/Advance already on the books (Dr that ledger, Cr Bank).",
  other: "Anything the categories above don't cover - e.g. a raw ledger adjustment. Not validated against Payment Nature; avoid using this for normal salary/vendor/payable transactions.",
};

// Phase 5 correction item 10 - lightweight party categorization alongside
// the free-text "Paid To" party name. Deliberately just a descriptive tag,
// not a real relationship to a Member/Vendor/Employee master record.
const PARTY_TYPE_LABELS = { vendor: "Vendor", member: "Member", employee: "Employee / Staff", other: "Other Party" };

// Phase 12 (Receipt Voucher) correction: a Receipt must never let the
// system silently decide which of several distinct accounting outcomes
// applies to the same money-received event - the user states this
// explicitly via Receipt Type, and the form then offers the ONE right
// field set for that choice, mirroring the Payment Nature pattern above.
const RECEIPT_TYPE_LABELS = {
  member: "Member Receipt",
  vendor_refund: "Vendor Refund",
  income: "Income Receipt",
  other: "Other Receipt",
};
const RECEIPT_TYPE_HELP = {
  member: "Money received from a Member/Flat against their outstanding maintenance dues (Dr Bank/Cash, Cr Member Receivables). Any amount beyond their outstanding is automatically posted as a Member Advance.",
  vendor_refund: "Money refunded back by a Vendor - e.g. an overpayment returned (Dr Bank/Cash, Cr the Vendor or the Liability/Advance/Expense ledger being reduced).",
  income: "Fresh income with no prior receivable - Bank Interest, Hall Income, Miscellaneous Income, etc. (Dr Bank/Cash, Cr the Income ledger).",
  other: "Anything the categories above don't cover - a raw ledger receipt. Avoid using this for a normal Member/Vendor/Income receipt.",
};

const PAYMENT_MODE_LABELS = {
  bank_transfer: "Bank Transfer", upi: "UPI", neft: "NEFT", rtgs: "RTGS", imps: "IMPS",
  cheque: "Cheque", cash: "Cash", card: "Card", other: "Other",
};

// Cheque needs a Cheque Number; the electronic-transfer modes ask for a
// transaction reference (UTR) but don't hard-require it; Cash needs neither.
const REFERENCE_LABEL_BY_MODE = {
  cheque: "Cheque No.", bank_transfer: "UTR / Transaction Ref.", upi: "UTR / Transaction Ref.",
  neft: "UTR / Transaction Ref.", rtgs: "UTR / Transaction Ref.", imps: "UTR / Transaction Ref.",
  card: "Transaction Ref.", cash: "", other: "Reference",
};
const REFERENCE_REQUIRED_MODES = new Set(["cheque"]);

// Phase 5 correction: the Recent Vouchers mini-table must not lump the
// business-facing party/purpose/bank sides into one ambiguous "Party"
// column - each voucher type gets its own honest 3-column breakdown,
// derived the same debit/credit-position way as the Voucher View page.
const TXN_COLUMN_LABELS = {
  PAYMENT: ["Paid To", "Payment For", "Paid From"],
  RECEIPT: ["Received From", "Received For", "Received Into"],
  CONTRA: ["Transferred To", "Purpose", "Transferred From"],
};

// Phase 16A item 6: "Open/reuse the existing voucher workflow with
// available details prefilled" - a statement row with no matching voucher
// (Bank Reconciliation's Import Bank Statement panel) links here with
// prefill* query params instead of a separate creation screen. Purely
// additive - every field defaults to the exact same blankByType shape below
// when no prefill params are present, so nothing changes for the normal
// "Payments"/"Receipts"/"Contra" nav entry points.
function buildBlankForm(voucherType, params) {
  const base = { ...blankByType[voucherType] };
  const amount = params.get("prefillAmount");
  const narration = params.get("prefillNarration");
  const referenceNo = params.get("prefillReferenceNo");
  const bankAccountId = params.get("prefillBankAccountId");
  const direction = params.get("prefillDirection"); // CONTRA only: "in" | "out"
  if (amount) base.amount = amount;
  if (narration) base.narration = narration;
  if (referenceNo) base.referenceNo = referenceNo;
  if (bankAccountId) {
    const selection = `bank:${bankAccountId}`;
    if (voucherType === "PAYMENT") base.payFrom = selection;
    if (voucherType === "RECEIPT") base.receiveInto = selection;
    if (voucherType === "CONTRA") { if (direction === "out") base.from = selection; else base.to = selection; }
  }
  return base;
}

const blankByType = {
  // partyVendorId is a purely transient UI field - it only drives which row
  // is highlighted in the Vendor search-picker used for Party Type=Vendor
  // Paid To entry, never submitted to the backend. The actual accounting
  // field is partyName (free text), since a voucher line's ledgerId/vendorId
  // slot is already occupied by the main Account/Payable selection above.
  PAYMENT: { paymentNature: "direct_expense", accountValue: "", payFrom: "", amount: "", partyName: "", partyType: "other", partyVendorId: "", mode: "bank_transfer", referenceNo: "", narration: "", useManualVoucherNumber: false, manualVoucherNumber: "" },
  // Phase 12: receiptType defaults to "member" - the most common real-world
  // Receipt for a Housing Society (maintenance collection) - never "other".
  RECEIPT: { receiptType: "member", receiveInto: "", accountValue: "", amount: "", partyName: "", partyType: "member", partyVendorId: "", mode: "bank_transfer", referenceNo: "", narration: "" },
  // Phase 13 (Contra & Journal Voucher): Contra now captures Payment Mode
  // and Reference/Cheque No. too, exactly like Payment/Receipt, instead of
  // being the odd one out with neither field.
  CONTRA: { from: "", to: "", amount: "", mode: "bank_transfer", referenceNo: "", narration: "" },
};

/**
 * Bank-account-aware voucher entry for Receipt, Payment and Contra - the
 * three "operational" voucher types that always involve a real bank account
 * or Cash on at least one side. Posts through the same Phase 3 engine used
 * by every other voucher (create-draft, then post), and never asks the user
 * to separately pick both a Bank Account and a matching ledger for the same
 * side of the entry - the backend resolves bankAccountId -> linkedLedgerId
 * itself. Journal/Vendor Bill/Credit/Debit Note intentionally keep the
 * advanced multi-line grid (GridVoucherFormPage) since those are general-
 * ledger or accrual entries, not single-transaction bank/cash movements.
 *
 * The "Expense / Payable Account" (Payment) and "Income / Receivable
 * Account" (Receipt) fields use the unified AccountSelect with Cash and Bank
 * Accounts hidden - so besides ordinary ledgers, a Vendor can be picked
 * directly there too. Picking a Vendor there is exactly "Vendor Payment":
 * the backend resolves the vendor's own linked payable ledger, so two
 * vendors sharing one generic "Vendor Payable" ledger is no longer forced.
 */
export default function BankVoucherFormPage({ voucherType, title, subtitle }) {
  const [searchParams] = useSearchParams();
  const [vouchers, setVouchers] = useState([]);
  const [form, setForm] = useState(() => buildBlankForm(voucherType, searchParams));
  const [date, setDate] = useState(() => searchParams.get("prefillDate") || new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nextVoucherNumber, setNextVoucherNumber] = useState("");
  const [payableOutstanding, setPayableOutstanding] = useState(null); // { ledgerName, balance, side } | null
  // Phase 12 (Receipt Voucher, Member Receipt type): the Member Receivables/
  // Advance ledger IDs, fetched once from the same Billing Ledger Mapping
  // settings the Billing<->Accounting integration already uses (Reuse
  // existing masters - no separate configuration is invented here).
  const [billingSettings, setBillingSettings] = useState(null);
  const [memberOutstanding, setMemberOutstanding] = useState(null); // { balance, side, hasHistory } | null

  // For the Payable Payment nature: once a Payable Account is picked, show
  // its current Outstanding honestly from a real derived balance - never a
  // fabricated or guessed number - so the user can see whether this payment
  // is reasonable before posting it (item 7 of the original correction).
  //
  // Phase 5 "Fix Payee/Party UX and Payable Linkage" correction (item 5):
  // this used to always use the ledger's OWN total balance, which is wrong
  // for a shared/control account like "Sweeper Payable" that carries several
  // unrelated people's dues at once - it could show one person's payment as
  // validated against a completely different person's outstanding. Now:
  //   - A real resolved Vendor (decoded.vendorId) still uses the ledger-wide
  //     balance - a Vendor has its own dedicated 1:1 ledger, so that IS its
  //     outstanding.
  //   - A raw/control ledger (decoded.ledgerId only) REQUIRES a Paid To /
  //     Payee (partyName) before any figure is shown at all - if none is
  //     entered yet, the panel says so plainly ("needs_party") instead of
  //     silently showing the ledger's unrelated total, and never uses a
  //     "could not be determined" message that might be misread as "no
  //     payable exists" or "a payable was confirmed".
  useEffect(() => {
    if (voucherType !== "PAYMENT" || form.paymentNature !== "payable_payment") { setPayableOutstanding(null); return; }
    const decoded = decodeAccountSelection(form.accountValue);
    if (!decoded) { setPayableOutstanding(null); return; }
    let cancelled = false;
    (async () => {
      try {
        if (decoded.vendorId) {
          const vendorRes = await api.get(`/society/accounting/engine/vendors/${decoded.vendorId}`);
          const ledgerId = vendorRes.data?.linkedLedgerId || "";
          if (!ledgerId) { if (!cancelled) setPayableOutstanding(null); return; }
          const balRes = await api.get(`/society/accounting/engine/ledgers/${ledgerId}/balance`).catch(() => ({ data: null }));
          if (cancelled) return;
          setPayableOutstanding({
            mode: "vendor",
            ledgerName: balRes.data?.ledgerName || vendorRes.data?.vendorName || "",
            partyName: vendorRes.data?.vendorName || "",
            balance: balRes.data ? Number(balRes.data.balance || 0) : null,
            side: balRes.data ? balRes.data.side : "",
            hasHistory: true,
          });
          return;
        }
        const ledgerId = decoded.ledgerId || "";
        if (!ledgerId) { if (!cancelled) setPayableOutstanding(null); return; }
        const partyName = (form.partyName || "").trim();
        if (!partyName) {
          const ledgerRes = await api.get(`/society/accounting/engine/ledgers/${ledgerId}/balance`).catch(() => ({ data: null }));
          if (cancelled) return;
          setPayableOutstanding({ mode: "needs_party", ledgerName: ledgerRes.data?.ledgerName || "", balance: null, side: "", hasHistory: false });
          return;
        }
        const balRes = await api.get(`/society/accounting/engine/ledgers/${ledgerId}/party-balance?partyName=${encodeURIComponent(partyName)}`).catch(() => ({ data: null }));
        if (cancelled) return;
        setPayableOutstanding({
          mode: "party",
          ledgerName: balRes.data?.ledgerName || "",
          partyName,
          balance: balRes.data ? Number(balRes.data.balance || 0) : null,
          side: balRes.data ? balRes.data.side : "",
          hasHistory: balRes.data ? !!balRes.data.hasHistory : false,
        });
      } catch {
        if (!cancelled) setPayableOutstanding(null);
      }
    })();
    return () => { cancelled = true; };
  }, [voucherType, form.paymentNature, form.accountValue, form.partyName]);

  // Phase 12 (Receipt Voucher): fetch the Society's Member Receivables /
  // Member Advance ledger configuration once, reusing the exact same
  // Billing Ledger Mapping settings endpoint the Billing<->Accounting
  // integration already relies on.
  useEffect(() => {
    if (voucherType !== "RECEIPT") return;
    (async () => {
      try {
        const res = await api.get("/society/accounting/billing-settings");
        setBillingSettings(res.data || null);
      } catch {
        setBillingSettings(null);
      }
    })();
  }, [voucherType]);

  // Phase 12 (Receipt Voucher, Member Receipt type): show the Member's real
  // current outstanding on the Member Receivables control ledger, using the
  // same generic party-balance endpoint the Payable Payment side already
  // uses for Vendors - never a fabricated figure. Member Receivables is an
  // ASSET-group ledger (Dr on a new Bill, Cr on a Payment), so - unlike the
  // Vendor/liability side above - a POSITIVE balance here IS the amount
  // outstanding; it is never negated.
  useEffect(() => {
    if (voucherType !== "RECEIPT" || form.receiptType !== "member") { setMemberOutstanding(null); return; }
    const ledgerId = billingSettings?.memberReceivableLedgerId;
    const memberLabel = (form.partyName || "").trim();
    if (!ledgerId || !memberLabel) { setMemberOutstanding(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/society/accounting/engine/ledgers/${ledgerId}/party-balance?partyName=${encodeURIComponent(memberLabel)}`).catch(() => ({ data: null }));
        if (!cancelled) setMemberOutstanding(res.data || null);
      } catch {
        if (!cancelled) setMemberOutstanding(null);
      }
    })();
    return () => { cancelled = true; };
  }, [voucherType, form.receiptType, form.partyName, billingSettings]);

  const load = async () => {
    try {
      const [res, numberingRes] = await Promise.all([
        api.get(`/society/accounting/engine/vouchers?voucherType=${voucherType}`).catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/settings/voucher-numbering").catch(() => ({ data: [] })),
      ]);
      setVouchers(Array.isArray(res.data) ? res.data : []);
      const numbering = (Array.isArray(numberingRes.data) ? numberingRes.data : []).find((row) => row.voucherType === voucherType);
      setNextVoucherNumber(numbering?.nextNumber || "");
    } catch (err) {
      toast.error(formatErr(err, "Could not load voucher data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setForm(buildBlankForm(voucherType, searchParams)); }, [voucherType]); // eslint-disable-line react-hooks/exhaustive-deps

  const postExistingDraft = async (v) => {
    try {
      await api.post(`/society/accounting/engine/vouchers/${v.id}/post`);
      toast.success(`${title} posted`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not post this voucher"));
    }
  };

  const deleteExistingDraft = async (v) => {
    if (!window.confirm(`Delete this draft ${title.toLowerCase()}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${v.id}`);
      toast.success("Draft deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this draft"));
    }
  };

  // Voucher Save/Edit/Delete Rules correction: a genuine hard delete for an
  // already-posted voucher, distinct from Cancel (which keeps full history
  // via an auto-reversal). Only offered here for Receipt (see the button
  // row below) - the backend (engine_delete_voucher) only allows it when
  // nothing depends on this voucher and reports the exact reason if not.
  const deletePostedVoucher = async (v) => {
    if (!window.confirm(`Permanently delete ${v.voucherNumber || "this voucher"}? Its accounting entries are removed completely and immediately - unlike Cancel, no reversal record is kept. This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${v.id}`);
      toast.success("Voucher permanently deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this voucher"));
    }
  };

  const cancelPostedVoucher = async (v) => {
    const reason = window.prompt(`Cancel ${v.voucherNumber}? This posts an automatic reversal and cannot be undone. Cancellation reason (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A cancellation reason is required"); return; }
    try {
      await api.post(`/society/accounting/engine/vouchers/${v.id}/cancel`, { reason: reason.trim() });
      toast.success("Voucher cancelled and reversed");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not cancel this voucher"));
    }
  };

  // Testing/setup-only escape hatch: permanently removes a cancelled voucher
  // and its reversal pair (never a still-posted one) so leftover test data
  // stops permanently blocking that ledger's Bank Account/Vendor/Fund from
  // being deleted or having its opening balance edited again. See the
  // backend endpoint's docstring for the full rationale.
  const forceDeleteVoucher = async (v) => {
    const reason = window.prompt(`Permanently delete ${v.voucherNumber} and its reversal voucher? This is for removing TEST data only and cannot be undone - it is different from Cancel and leaves no trace. Reason (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    if (!window.confirm(`Really permanently delete ${v.voucherNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${v.id}/force-delete`, { params: { reason: reason.trim() } });
      toast.success("Voucher permanently deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this voucher"));
    }
  };

  const buildLines = () => {
    const amount = Number(form.amount || 0);
    if (voucherType === "PAYMENT") {
      const bankSide = decodeBankAccountSelection(form.payFrom);
      const accountSide = decodeAccountSelection(form.accountValue);
      if (!accountSide || !bankSide) return null;
      return [
        { ...accountSide, debit: amount, credit: 0, partyName: form.partyName, partyType: form.partyType, referenceNo: form.referenceNo },
        { ...bankSide, debit: 0, credit: amount },
      ];
    }
    if (voucherType === "RECEIPT") {
      const bankSide = decodeBankAccountSelection(form.receiveInto);
      const accountSide = decodeAccountSelection(form.accountValue);
      if (!bankSide || !accountSide) return null;
      return [
        { ...bankSide, debit: amount, credit: 0 },
        { ...accountSide, debit: 0, credit: amount, partyName: form.partyName, partyType: form.partyType, referenceNo: form.referenceNo },
      ];
    }
    if (voucherType === "CONTRA") {
      const fromSide = decodeBankAccountSelection(form.from);
      const toSide = decodeBankAccountSelection(form.to);
      if (!fromSide || !toSide) return null;
      return [
        { ...toSide, debit: amount, credit: 0 },
        { ...fromSide, debit: 0, credit: amount },
      ];
    }
    return null;
  };

  const referenceRequired = REFERENCE_REQUIRED_MODES.has(form.mode) && !form.referenceNo.trim();

  const guidedExpenseNature = voucherType === "PAYMENT" && ["emergency_expense", "salary_payment", "reimbursement"].includes(form.paymentNature);

  const isValid = () => {
    const amount = Number(form.amount || 0);
    if (!(amount > 0)) return false;
    if (referenceRequired) return false;
    if (voucherType === "PAYMENT") {
      if (form.useManualVoucherNumber && !form.manualVoucherNumber.trim()) return false;
      if (guidedExpenseNature && (!form.partyName.trim() || !form.narration.trim())) return false;
      return !!form.accountValue && !!form.payFrom;
    }
    if (voucherType === "RECEIPT") {
      if (!form.receiveInto) return false;
      // Phase 12: Member Receipt uses the Member/Flat search field
      // (form.partyName) instead of the generic ledger AccountSelect - the
      // other three Receipt Types still need an Account selected.
      if (form.receiptType === "member") return !!form.partyName.trim();
      return !!form.accountValue;
    }
    if (voucherType === "CONTRA") return !!form.from && !!form.to && form.from !== form.to;
    return false;
  };

  const validateBeforeSave = () => {
    if (!isValid()) {
      if (referenceRequired) { toast.error(`${REFERENCE_LABEL_BY_MODE[form.mode] || "Reference"} is required for this payment mode`); return false; }
      if (voucherType === "PAYMENT" && form.useManualVoucherNumber && !form.manualVoucherNumber.trim()) { toast.error("Enter the manual Voucher Number or switch back to automatic numbering"); return false; }
      if (voucherType === "RECEIPT" && form.receiptType === "member") { toast.error("Select a Member/Flat, Receive Into account and Amount"); return false; }
      if (guidedExpenseNature) { toast.error("Select the expense and payment accounts, enter the payee, amount and a clear narration"); return false; }
      toast.error(voucherType === "CONTRA" ? "Choose two different accounts and an amount" : "Fill in the account and amount");
      return false;
    }
    return true;
  };

  // Phase 5 correction (Payment only now - see the Voucher Save/Edit/Delete
  // Rules correction for why Receipt no longer uses this, and Phase 13 for
  // why Contra no longer does either): two clearly separate actions -
  // "Save Draft" never posts - the voucher stays editable/deletable and does
  // not consume a voucher number. "Post Voucher" creates the draft and
  // immediately posts it in one step, and the user is told plainly which
  // one happened rather than being left to guess from a single ambiguous
  // "Save" button.
  const saveDraft = async () => {
    if (!validateBeforeSave()) return;
    const lines = buildLines();
    if (!lines) { toast.error("Could not build this voucher - check the selected accounts"); return; }
    try {
      setBusy(true);
      await api.post("/society/accounting/engine/vouchers", {
        voucherType, voucherDate: date, narration: form.narration || "", referenceNo: form.referenceNo || "",
        paymentMode: form.mode || "", paymentNature: voucherType === "PAYMENT" ? form.paymentNature : "other",
        manualVoucherNumber: voucherType === "PAYMENT" && form.useManualVoucherNumber ? form.manualVoucherNumber.trim() : "", lines,
      });
      toast.success(`${title} saved as DRAFT. It is not posted to the books until you Post it.`);
      setForm(blankByType[voucherType]);
      await load();
    } catch (err) {
      toast.error(formatErr(err, `Could not save ${title.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  const saveAndPost = async () => {
    if (!validateBeforeSave()) return;
    const lines = buildLines();
    if (!lines) { toast.error("Could not build this voucher - check the selected accounts"); return; }
    try {
      setBusy(true);
      const draft = await api.post("/society/accounting/engine/vouchers", {
        voucherType, voucherDate: date, narration: form.narration || "", referenceNo: form.referenceNo || "",
        paymentMode: form.mode || "", paymentNature: voucherType === "PAYMENT" ? form.paymentNature : "other",
        manualVoucherNumber: voucherType === "PAYMENT" && form.useManualVoucherNumber ? form.manualVoucherNumber.trim() : "", lines,
      });
      try {
        await api.post(`/society/accounting/engine/vouchers/${draft.data.id}/post`);
        toast.success(`${title} POSTED`);
      } catch (postErr) {
        toast.error(formatErr(postErr, `Saved as a DRAFT but could not post automatically. Open Administration → Financial Years to check it covers ${date}, then post it from All Vouchers.`));
      }
      setForm(blankByType[voucherType]);
      await load();
    } catch (err) {
      toast.error(formatErr(err, `Could not save ${title.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  // Voucher Save/Edit/Delete Rules correction: a Receipt must never present
  // a Draft choice - saving one creates the real accounting transaction
  // immediately, with no separate Post/Approve step. Member Receipt goes
  // through the dedicated split-aware endpoint (server-computed outstanding
  // -> Member Receivables / Member Advance split, atomic and race-free);
  // Vendor Refund/Income/Other Receipt reuse the exact same generic
  // create-then-post chain saveAndPost already uses for Payment/Contra.
  const saveReceipt = async () => {
    if (!validateBeforeSave()) return;
    try {
      setBusy(true);
      if (form.receiptType === "member") {
        const decodedBank = decodeBankAccountSelection(form.receiveInto);
        if (!decodedBank) { toast.error("Select where this receipt is being received into"); return; }
        await api.post("/society/accounting/engine/vouchers/receipt/member", {
          memberLabel: form.partyName.trim(), amount: Number(form.amount || 0),
          bankAccountId: decodedBank.bankAccountId || null, cashLedgerId: decodedBank.ledgerId || null,
          paymentMode: form.mode || "", referenceNo: form.referenceNo || "", voucherDate: date, narration: form.narration || "",
        });
      } else {
        const lines = buildLines();
        if (!lines) { toast.error("Could not build this voucher - check the selected accounts"); return; }
        const draft = await api.post("/society/accounting/engine/vouchers", {
          voucherType, voucherDate: date, narration: form.narration || "", referenceNo: form.referenceNo || "",
          paymentMode: form.mode || "", paymentNature: "other", lines,
        });
        await api.post(`/society/accounting/engine/vouchers/${draft.data.id}/post`);
      }
      toast.success(`${title} saved`);
      setForm(blankByType[voucherType]);
      await load();
    } catch (err) {
      toast.error(formatErr(err, `Could not save ${title.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  // Phase 13 (Contra & Journal Voucher): Contra also gets no mandatory
  // Draft/Post workflow - Save creates the immediately-posted fund transfer
  // in one action, exactly like Receipt above. Reuses the exact same
  // generic create-then-post chain saveAndPost already used - no parallel
  // accounting logic - just without ever surfacing the Draft option.
  const saveContra = async () => {
    if (!validateBeforeSave()) return;
    const lines = buildLines();
    if (!lines) { toast.error("Could not build this transfer - check the selected accounts"); return; }
    try {
      setBusy(true);
      const draft = await api.post("/society/accounting/engine/vouchers", {
        voucherType, voucherDate: date, narration: form.narration || "", referenceNo: form.referenceNo || "",
        paymentMode: form.mode || "", paymentNature: "other", lines,
      });
      await api.post(`/society/accounting/engine/vouchers/${draft.data.id}/post`);
      toast.success(`${title} saved`);
      setForm(blankByType[voucherType]);
      await load();
    } catch (err) {
      toast.error(formatErr(err, `Could not save ${title.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  // Switching Payment Nature always clears the previously-picked account -
  // a ledger chosen under "Direct Expense" must never silently carry over
  // and be reinterpreted as a Vendor once the nature changes, and vice
  // versa; forcing a fresh pick keeps the two concepts from ever mixing.
  const setPaymentNature = (nature) => setForm((f) => ({
    ...f, paymentNature: nature, accountValue: "", partyName: "", partyVendorId: "",
    partyType: nature === "salary_payment" ? "employee" : nature === "reimbursement" ? "member" : "other",
  }));
  // Same principle for Receipt Type - switching away from Member Receipt
  // must never leave a stale Member/Flat name attached to a Vendor Refund/
  // Income/Other Receipt, and vice versa.
  const setReceiptType = (type) => setForm((f) => ({
    ...f, receiptType: type, accountValue: "", partyName: "", partyVendorId: "",
    partyType: type === "member" ? "member" : type === "vendor_refund" ? "vendor" : "other",
  }));
  const referenceLabel = REFERENCE_LABEL_BY_MODE[form.mode] || "Reference";
  const shortTitle = title.replace(/\s*Voucher$/i, "");

  // Phase 5 "Fix Payee/Party UX and Payable Linkage" correction (item 3): if
  // the selected Payable Account resolves to a real Vendor entity, that
  // vendor's own identity already IS the Paid To - deriving/restricting it
  // this way means an unrelated arbitrary payee can never be attached to a
  // party-specific payable settlement, since there is simply no separate
  // Paid To field to type one into.
  const decodedPaymentAccount = voucherType === "PAYMENT" ? decodeAccountSelection(form.accountValue) : null;
  // Phase 15 (Vendor Bills & Payables) item 4: a Vendor Advance now also
  // resolves to a real Vendor (its own dedicated ledger, per the Payment
  // Nature correction above) - same "no separate Paid To" treatment as
  // Payable Payment, for the same reason (the vendor's identity already IS
  // the payee; a separate free-text field could never legitimately disagree
  // with it).
  const payeeDerivedFromVendor = voucherType === "PAYMENT" && (form.paymentNature === "payable_payment" || form.paymentNature === "advance_payment") && !!decodedPaymentAccount?.vendorId;

  // Phase 12 (Receipt Voucher): same principle for a Vendor Refund whose
  // Vendor/Refund Account resolves to a real Vendor entity - that vendor's
  // own identity already IS the "Received From", so no separate free-text
  // Received From field is needed (or should be shown) in that case.
  const decodedReceiptAccount = voucherType === "RECEIPT" ? decodeAccountSelection(form.accountValue) : null;
  const receiptPayeeDerivedFromVendor = voucherType === "RECEIPT" && form.receiptType === "vendor_refund" && !!decodedReceiptAccount?.vendorId;

  // Phase 5 "Fix Payee/Party UX and Payable Linkage" correction (items 1, 2,
  // 7): the Paid To/Payee widget itself now depends on Party Type instead of
  // always being the Member/Flat search box - a Vendor searches the Vendor
  // master (name-only autocomplete, see VendorSelect's onSelectVendor), an
  // Employee/Staff gets controlled free-text entry (no dedicated staff/payee
  // master exists yet), a Member still gets the Member/Flat search (whose
  // own pick() already renders "Name — Flat Wing-Number", satisfying item 7),
  // and Other Party is plain free text. "Search member or flat" must never
  // appear for Vendor, Employee/Staff or Other Party.
  const renderPayeeFields = () => {
    if (payeeDerivedFromVendor || receiptPayeeDerivedFromVendor) return null;
    const label = voucherType === "RECEIPT" ? "Received From" : "Paid To / Payee";
    return (
      <>
        <Field label="Party Type">
          <Select value={form.partyType} onValueChange={(v) => setForm((f) => ({ ...f, partyType: v, partyName: "", partyVendorId: "" }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PARTY_TYPE_LABELS).map(([value, l]) => <SelectItem key={value} value={value}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={label}>
          {form.partyType === "vendor" && (
            <VendorSelect
              value={form.partyVendorId}
              onChange={(id) => setField("partyVendorId", id)}
              onSelectVendor={(v) => setField("partyName", v ? v.vendorName : "")}
              placeholder="Search/select vendor"
            />
          )}
          {form.partyType === "employee" && (
            <Input value={form.partyName} onChange={(e) => setField("partyName", e.target.value)} placeholder="Enter staff/employee name" />
          )}
          {form.partyType === "member" && (
            <PartySearchInput value={form.partyName} onChange={(v) => setField("partyName", v)} />
          )}
          {form.partyType === "other" && (
            <Input value={form.partyName} onChange={(e) => setField("partyName", e.target.value)} placeholder="Enter payee name" />
          )}
        </Field>
      </>
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-[#6B7280]">{subtitle}</p>}
      </div>

      <SectionCard title="Voucher entry">
        {voucherType === "PAYMENT" && (
          <div className="mb-4 rounded-sm border border-sky-200 bg-sky-50 p-3 text-sm text-slate-700">
            <span className="font-semibold text-slate-900">Received a supplier invoice?</span>{" "}
            Enter it first under <Link className="font-semibold text-blue-700 underline" to={`${ACCOUNTING_BASE}/vendor-bills`}>Purchase / Vendor Bill</Link>, then use <span className="font-semibold">Payable Payment</span> here when paying it. For salary, reimbursement or genuine no-invoice work, choose the matching payment type below.
          </div>
        )}
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Date">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>

          {voucherType === "PAYMENT" && (
            <>
              <div className="grid gap-3 md:col-span-3 md:grid-cols-2">
                <Field label="Voucher Number">
                  <Input
                    value={form.useManualVoucherNumber ? form.manualVoucherNumber : (nextVoucherNumber || "Loading...")}
                    disabled={!form.useManualVoucherNumber}
                    onChange={(e) => setField("manualVoucherNumber", e.target.value.toUpperCase())}
                  />
                  <p className="mt-1 text-xs text-[#6B7280]">
                    {form.useManualVoucherNumber ? "The number is reserved on Save Draft and checked again on Posting." : "Preview only — the automatic number is assigned when posted."}
                  </p>
                </Field>
                <label className="mt-6 flex h-10 items-center gap-2 rounded-sm border border-[#E5E7EB] px-3 text-sm text-[#374151]">
                  <input
                    type="checkbox"
                    checked={form.useManualVoucherNumber}
                    onChange={(e) => setForm((f) => ({ ...f, useManualVoucherNumber: e.target.checked, manualVoucherNumber: "" }))}
                  />
                  Enter voucher number manually
                </label>
              </div>
              <div className="md:col-span-3">
                <Field label="Payment Nature">
                  <Select value={form.paymentNature} onValueChange={setPaymentNature}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PAYMENT_NATURE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <p className="mt-1 text-xs text-[#6B7280]">{PAYMENT_NATURE_HELP[form.paymentNature]}</p>
              </div>

              {form.paymentNature === "direct_expense" && (
                <Field label="Expense Account">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts hideVendors context="paymentDirectExpense" placeholder="Search expense ledger" searchPlaceholder="Search expense ledger..." />
                </Field>
              )}

              {["emergency_expense", "salary_payment", "reimbursement"].includes(form.paymentNature) && (
                <Field label={form.paymentNature === "salary_payment" ? "Salary Expense Account" : "Expense Account"}>
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts hideVendors context="paymentDirectExpense" placeholder="Search expense ledger" searchPlaceholder="Search expense ledger..." />
                </Field>
              )}

              {form.paymentNature === "payable_payment" && (
                <Field label="Payable / Vendor Account">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts context="paymentPayable" placeholder="Search vendor or payable ledger" searchPlaceholder="Search vendor or payable ledger (e.g. Sweeper Payable)..." />
                </Field>
              )}

              {form.paymentNature === "advance_payment" && (
                <Field label="Advance Account (or a Vendor, for a Vendor Advance)">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts context="paymentAdvance" placeholder="Search advance ledger or vendor" searchPlaceholder="Search advance ledger or vendor..." />
                </Field>
              )}

              {form.paymentNature === "capital_payment" && (
                <Field label="Asset Account">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts hideVendors context="paymentCapital" placeholder="Search asset/investment ledger" searchPlaceholder="Search asset/investment ledger..." />
                </Field>
              )}

              {form.paymentNature === "refund" && (
                <Field label="Refund Against">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts context="paymentRefund" placeholder="Search member, vendor, liability or advance ledger" searchPlaceholder="Search member, vendor, liability or advance ledger..." />
                </Field>
              )}

              {form.paymentNature === "other" && (
                <Field label="Account (ledger, vendor or payable)">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts context="paymentDebit" placeholder="Search ledger or vendor" searchPlaceholder="Search ledger or vendor..." />
                </Field>
              )}

              {/* Phase 5 "Fix Payee/Party UX and Payable Linkage" correction
                  (item 6): Payable/Vendor Account -> Paid To/Payee ->
                  Outstanding -> Amount -> Pay From, in that order - Paid To
                  is resolved BEFORE Outstanding is looked up because, for a
                  shared/control payable account, the outstanding figure is
                  meaningless without knowing which party it's for. */}
              {renderPayeeFields()}

              {form.paymentNature === "payable_payment" && payableOutstanding && (() => {
                const amount = Number(form.amount || 0);
                if (payableOutstanding.mode === "needs_party") {
                  return (
                    <div className="rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-xs text-[#374151] md:col-span-3">
                      <div><span className="font-semibold">Payable Account:</span> {payableOutstanding.ledgerName || "-"}</div>
                      <div className="mt-1 text-amber-700">
                        This is a shared/control payable account that can carry more than one party's dues. Enter Paid To / Payee above to see and validate the outstanding for that specific party - it is never shown as this ledger's overall total.
                      </div>
                    </div>
                  );
                }
                const hasRealPayable = payableOutstanding.balance !== null && payableOutstanding.side === "credit" && payableOutstanding.balance < 0 && payableOutstanding.hasHistory !== false;
                const outstandingAbs = hasRealPayable ? Math.abs(payableOutstanding.balance) : 0;
                const remaining = outstandingAbs - amount;
                const partyLabel = payableOutstanding.partyName || payableOutstanding.ledgerName;
                return (
                  <div className="rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-xs text-[#374151] md:col-span-3">
                    <div><span className="font-semibold">Payable Account:</span> {payableOutstanding.ledgerName || "-"}</div>
                    {payableOutstanding.balance === null ? (
                      <div className="mt-1 text-amber-700">Outstanding could not be looked up right now - try again before posting.</div>
                    ) : !hasRealPayable ? (
                      <div className="mt-1 text-amber-700">
                        No outstanding payable found for '{partyLabel}'{payableOutstanding.mode === "party" ? ` under '${payableOutstanding.ledgerName}'` : ""} (current balance: {formatCurrency(Math.abs(payableOutstanding.balance))} {payableOutstanding.side}).
                        Confirm this is really a Payable Payment - it may instead be a Direct Expense or Refund.
                      </div>
                    ) : (
                      <>
                        {payableOutstanding.mode === "party" && <div><span className="font-semibold">Party/Subledger:</span> {partyLabel} (under {payableOutstanding.ledgerName})</div>}
                        <div><span className="font-semibold">Outstanding Before:</span> {formatCurrency(outstandingAbs)}</div>
                        {amount > 0 && (
                          <>
                            <div><span className="font-semibold">Amount Paid:</span> {formatCurrency(amount)}</div>
                            <div className={remaining < 0 ? "font-semibold text-red-600" : ""}>
                              <span className="font-semibold">Outstanding After:</span> {formatCurrency(Math.abs(remaining))}{remaining < 0 ? " (exceeds outstanding!)" : ""}
                            </div>
                            {payableOutstanding.mode === "party" && remaining >= 0 && (
                              <div className="mt-1">Party/Subledger: {partyLabel} {formatCurrency(amount)} settlement</div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              <Field label="Amount">
                <Input type="number" min="1" value={form.amount} onChange={(e) => setField("amount", e.target.value)} />
              </Field>

              <Field label="Pay From">
                <BankAccountSelect value={form.payFrom} onChange={(v) => setField("payFrom", v)} placeholder="Select Cash or a Bank Account" />
              </Field>
            </>
          )}

          {voucherType === "RECEIPT" && (
            <>
              <div className="md:col-span-3">
                <Field label="Receipt Type">
                  <Select value={form.receiptType} onValueChange={setReceiptType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(RECEIPT_TYPE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <p className="mt-1 text-xs text-[#6B7280]">{RECEIPT_TYPE_HELP[form.receiptType]}</p>
              </div>

              <Field label="Receive Into">
                <BankAccountSelect value={form.receiveInto} onChange={(v) => setField("receiveInto", v)} placeholder="Select Cash or a Bank Account" />
              </Field>

              {form.receiptType === "member" && (
                <Field label="Member / Flat">
                  <PartySearchInput value={form.partyName} onChange={(v) => setField("partyName", v)} />
                </Field>
              )}
              {form.receiptType === "vendor_refund" && (
                <Field label="Vendor / Refund Account">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts context="receiptVendorRefund" placeholder="Search vendor or refund ledger" searchPlaceholder="Search vendor or refund ledger..." />
                </Field>
              )}
              {form.receiptType === "income" && (
                <Field label="Income Ledger">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts hideVendors context="receiptIncome" placeholder="Search income ledger" searchPlaceholder="Search income ledger (e.g. Bank Interest, Hall Income)..." />
                </Field>
              )}
              {form.receiptType === "other" && (
                <Field label="Account">
                  <AccountSelect value={form.accountValue} onChange={(v) => setField("accountValue", v)} hideCash hideBankAccounts context="receiptCredit" placeholder="Search ledger" searchPlaceholder="Search ledger..." />
                </Field>
              )}

              {form.receiptType !== "member" && renderPayeeFields()}

              {/* Phase 12: honest, server-derived outstanding for the
                  selected Member - never a guessed/fabricated figure - and
                  a clear preview of how this amount will split between
                  Member Receivables and Member Advance before Save. */}
              {form.receiptType === "member" && form.partyName.trim() && (
                <div className="rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-xs text-[#374151] md:col-span-3">
                  {!billingSettings?.memberReceivableLedgerId ? (
                    <div className="text-[#6B7280]">Loading the member balance...</div>
                  ) : !memberOutstanding ? (
                    <div className="text-[#6B7280]">Looking up outstanding for {form.partyName}...</div>
                  ) : (() => {
                    // Member Receivables is ASSET-group (Dr on a Bill, Cr on
                    // a Payment) - a positive balance IS the outstanding,
                    // never negated (see create_member_receipt_voucher).
                    const outstanding = memberOutstanding.balance > 0 ? memberOutstanding.balance : 0;
                    const amount = Number(form.amount || 0);
                    const applied = Math.min(amount, outstanding);
                    const excess = Math.max(0, amount - outstanding);
                    return (
                      <>
                        <div><span className="font-semibold">Outstanding Before:</span> {formatCurrency(outstanding)}</div>
                        {amount > 0 && (
                          <>
                            <div><span className="font-semibold">Applied to Member Receivables:</span> {formatCurrency(applied)}</div>
                            {excess > 0 && (
                              billingSettings?.memberAdvanceLedgerId ? (
                                <div><span className="font-semibold">Excess → Member Advance:</span> {formatCurrency(excess)}</div>
                              ) : (
                                <div className="mt-1 font-semibold text-emerald-700">Member credit after receipt: {formatCurrency(excess)}</div>
                              )
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </>
          )}

          {voucherType === "CONTRA" && (
            <>
              <Field label="Transfer From">
                <BankAccountSelect value={form.from} onChange={(v) => setField("from", v)} excludeValue={form.to} placeholder="Select Cash or a Bank Account" />
              </Field>
              <Field label="Transfer To">
                <BankAccountSelect value={form.to} onChange={(v) => setField("to", v)} excludeValue={form.from} placeholder="Select Cash or a Bank Account" />
              </Field>
            </>
          )}

          {voucherType === "RECEIPT" && (
            <Field label="Amount">
              <Input type="number" min="1" value={form.amount} onChange={(e) => setField("amount", e.target.value)} />
            </Field>
          )}
          {voucherType === "CONTRA" && (
            <Field label="Amount">
              <Input type="number" min="1" value={form.amount} onChange={(e) => setField("amount", e.target.value)} />
            </Field>
          )}

          <Field label="Payment Mode">
            <Select value={form.mode} onValueChange={(v) => setField("mode", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PAYMENT_MODE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {referenceLabel && (
            <Field label={voucherType === "CONTRA" ? "Reference / Cheque No." : referenceLabel}>
              <Input value={form.referenceNo} onChange={(e) => setField("referenceNo", e.target.value)} />
            </Field>
          )}

          <div className="md:col-span-3">
            <Field label={guidedExpenseNature ? "Narration / Supporting Details (required)" : "Narration"}>
              <Textarea rows={guidedExpenseNature ? 3 : 2} value={form.narration} onChange={(e) => setField("narration", e.target.value)} placeholder={
                form.paymentNature === "emergency_expense" ? "Describe the urgent work, location, reason no supplier invoice was available and approval details" :
                form.paymentNature === "salary_payment" ? "Salary for month, employee role and any deduction/reference details" :
                form.paymentNature === "reimbursement" ? "Expense purpose, original payer and supporting receipt/details" : ""
              } />
            </Field>
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            {voucherType === "RECEIPT" ? (
              // Voucher Save/Edit/Delete Rules correction: Receipt has no
              // Draft/Post choice at all - Save creates the final,
              // immediately-posted accounting transaction in one action.
              <Button disabled={busy || !isValid()} onClick={saveReceipt} className="flex-1 bg-[#111827] text-white hover:bg-[#374151]">
                <Plus className="mr-2 h-4 w-4" /> Save Receipt
              </Button>
            ) : voucherType === "CONTRA" ? (
              // Phase 13 (Contra & Journal Voucher): Contra has no Draft/Post
              // choice either, same principle as Receipt - Save transfers the
              // funds and creates the posted accounting entry immediately.
              <Button disabled={busy || !isValid()} onClick={saveContra} className="flex-1 bg-[#111827] text-white hover:bg-[#374151]">
                <Plus className="mr-2 h-4 w-4" /> Save Transfer
              </Button>
            ) : (
              <>
                <Button variant="outline" disabled={busy || !isValid()} onClick={saveDraft} className="flex-1">
                  <Plus className="mr-2 h-4 w-4" /> Save Draft
                </Button>
                <Button disabled={busy || !isValid()} onClick={saveAndPost} className="flex-1 bg-[#111827] text-white hover:bg-[#374151]">
                  Post Voucher
                </Button>
              </>
            )}
          </div>
        </div>
        <p className="mt-2 text-xs text-[#6B7280]">
          {voucherType === "RECEIPT"
            ? "Saving this Receipt creates the accounting transaction immediately and updates the General Ledger, Bank/Cash Book, Trial Balance and outstanding balances right away - it is not a draft."
            : voucherType === "CONTRA"
            ? "Saving this Contra creates the accounting transaction immediately and updates both Bank/Cash Books right away - it is not a draft."
            : "Save Draft keeps this editable and does not assign a voucher number. Post Voucher saves and posts to the books immediately."}
        </p>
      </SectionCard>

      <SectionCard title={`Recent ${shortTitle} Vouchers`}>
        <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead>
                {TXN_COLUMN_LABELS[voucherType].map((label) => <TableHead key={label}>{label}</TableHead>)}
                <TableHead>Reference</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : vouchers.slice(0, 20).map((v) => {
                const txn = deriveTransactionDetails(v);
                const fallbackParty = (v.lines || []).find((l) => l.partyName)?.partyName || "-";
                return (
                  <TableRow key={v.id}>
                    <TableCell>{v.voucherDate}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {v.voucherNumber || <span className="rounded-sm bg-[#F3F4F6] px-1.5 py-0.5 font-sans text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">Draft</span>}
                    </TableCell>
                    <TableCell>{txn ? txn.paidTo : fallbackParty}</TableCell>
                    <TableCell>{txn ? txn.paymentFor : "-"}</TableCell>
                    <TableCell>{txn ? txn.paidFrom : "-"}</TableCell>
                    <TableCell>{v.referenceNo || "-"}</TableCell>
                    <TableCell className="capitalize">{v.status}</TableCell>
                    <TableCell className="text-right">{formatCurrency(v.totalDebit)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold">
                        <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}`} className="text-[#111827] hover:underline">View</Link>
                        {v.status === "draft" && (
                          <>
                            <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                            <button type="button" onClick={() => postExistingDraft(v)} className="text-[#111827] hover:underline">Post</button>
                            <button type="button" onClick={() => deleteExistingDraft(v)} className="text-red-600 hover:underline">Delete</button>
                          </>
                        )}
                        {v.status === "posted" && (
                          <>
                            {/* Voucher Save/Edit/Delete Rules correction - originally
                                scoped to Receipt only (not Payment/Contra, per the then-
                                current "do not implement ... Contra Voucher" scope
                                limit). Phase 13 extends this to Contra too, per its own
                                explicit item 4 (Edit/Delete). Payment is intentionally
                                still out of scope here (never asked for). Edit/Delete
                                are always shown; the backend decides safety and returns
                                the exact reason if blocked - see engine_update_voucher /
                                engine_delete_voucher. */}
                            {(voucherType === "RECEIPT" || voucherType === "CONTRA") && (
                              <>
                                <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                                <button type="button" onClick={() => deletePostedVoucher(v)} className="text-red-600 hover:underline">Delete</button>
                              </>
                            )}
                            {/* Phase 13 (Journal/Contra correction), item 3: Contra has
                                no Draft/Post/Cancel lifecycle left at all - Edit/Delete
                                above are the only actions. Payment and Receipt keep
                                Cancel (Payment's Draft/Post/Cancel workflow is untouched;
                                Receipt's own Cancel was never asked to be removed by
                                either correction). */}
                            {voucherType !== "CONTRA" && (
                              <button type="button" onClick={() => cancelPostedVoucher(v)} className="text-red-600 hover:underline">Cancel</button>
                            )}
                          </>
                        )}
                        {v.status === "cancelled" && (
                          <button type="button" onClick={() => forceDeleteVoucher(v)} className="text-red-600 hover:underline" title="Testing only: permanently removes this voucher and its reversal">Force delete</button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && vouchers.length === 0 && (
                <TableRow><TableCell colSpan={9} className="py-8 text-center text-[#6B7280]">No {title.toLowerCase()} vouchers yet. Create your first {title.toLowerCase()} above.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
    </div>
  );
}
